import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../hooks/ThemeContext';
import { CartContext } from '../hooks/CartContext';
import SectionTitle from '../components/common/SectionTitle';
import LoadingSpinner from '../components/globals/LoadingSpinner';

const Cart = () => {
  const { theme } = useContext(ThemeContext);
  const { cart, loading, error, updateQuantity, removeFromCart, clearCart } = useContext(CartContext);
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  if (loading) {
    return (
      <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
        <div className="flex justify-center items-center py-20">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  const handleQuantityChange = async (itemId, newQuantity) => {
    if (newQuantity < 1) {
      await removeFromCart(itemId);
    } else {
      await updateQuantity(itemId, newQuantity);
    }
  };

  const handleRemoveItem = async (itemId) => {
    await removeFromCart(itemId);
  };

  const handleClearCart = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir vider votre panier ?')) {
      await clearCart();
    }
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  return (
    <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
      <div className="container mx-auto px-4 py-12 max-w-5xl">
        
        {/* En-tête */}
        <SectionTitle 
          title="Votre Panier"
          subtitle="Gérez vos articles avant de procéder au paiement"
          align="left"
        />

        {/* Message d'erreur */}
        {error && (
          <div className={`p-4 rounded-lg mb-6 ${isDark ? 'bg-red-500/20 text-red-300 border border-red-500/30' : 'bg-red-50 text-red-800 border border-red-200'}`}>
            {error}
          </div>
        )}

        {/* Panier vide */}
        {!loading && (!cart.items || cart.items.length === 0) ? (
          <div className={`text-center py-12 ${isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'} rounded-lg`}>
            <div className="text-5xl mb-4">🛒</div>
            <h3 className="text-2xl font-semibold mb-2">Panier vide</h3>
            <p className={`mb-6 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
              Vous n'avez pas encore ajouté de produits à votre panier.
            </p>
            <button
              onClick={() => navigate('/shop')}
              className="inline-block px-6 py-3 bg-rose-600 text-white rounded-lg font-medium hover:bg-rose-700 transition-colors"
            >
              Découvrir nos créations
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Liste des articles */}
            <div className="lg:col-span-2">
              <div className={`rounded-lg overflow-hidden ${isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'}`}>
                
                {/* En-tête du tableau */}
                <div className={`grid grid-cols-4 gap-4 p-6 border-b ${isDark ? 'border-white/10 bg-[#0a0a0c]' : 'border-gray-100 bg-gray-50'}`}>
                  <div className="col-span-2 font-semibold">Produit</div>
                  <div className="font-semibold text-center">Quantité</div>
                  <div className="font-semibold text-right">Prix</div>
                </div>

                {/* Articles */}
                <div className="divide-y" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#e5e7eb' }}>
                  {cart.items.map((item) => (
                    <div key={item.id} className={`grid grid-cols-4 gap-4 p-6 items-center hover:${isDark ? 'bg-[#151516]' : 'bg-gray-50'} transition-colors`}>
                      
                      {/* Produit */}
                      <div className="col-span-2">
                        <h4 className="font-semibold mb-1">{item.product?.name || 'Produit'}</h4>
                        <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                          {item.product?.id_product && `ID: ${item.product.id_product}`}
                        </p>
                      </div>

                      {/* Quantité */}
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          className={`px-2 py-1 rounded text-sm font-medium transition-colors ${
                            isDark
                              ? 'bg-[#151516] text-white hover:bg-rose-600'
                              : 'bg-gray-100 text-gray-800 hover:bg-rose-600 hover:text-white'
                          }`}
                        >
                          −
                        </button>
                        <span className="w-8 text-center font-semibold">{item.quantity}</span>
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                          className={`px-2 py-1 rounded text-sm font-medium transition-colors ${
                            isDark
                              ? 'bg-[#151516] text-white hover:bg-rose-600'
                              : 'bg-gray-100 text-gray-800 hover:bg-rose-600 hover:text-white'
                          }`}
                        >
                          +
                        </button>
                      </div>

                      {/* Prix et suppression */}
                      <div className="text-right">
                        <p className="font-semibold mb-2">${item.price?.toFixed(2) || '0.00'}</p>
                        <button
                          onClick={() => handleRemoveItem(item.id)}
                          className="text-sm text-rose-500 hover:text-rose-600 font-medium transition-colors"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div className={`p-6 border-t text-right ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                  <button
                    onClick={handleClearCart}
                    className={`text-sm font-medium transition-colors ${
                      isDark
                        ? 'text-white/60 hover:text-rose-400'
                        : 'text-gray-600 hover:text-rose-600'
                    }`}
                  >
                    Vider le panier
                  </button>
                </div>
              </div>
            </div>

            {/* Résumé et checkout */}
            <div className="lg:col-span-1">
              <div className={`rounded-lg p-6 sticky top-24 ${isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'}`}>
                
                <h3 className="text-lg font-semibold mb-6">Résumé de la commande</h3>

                {/* Détails */}
                <div className={`space-y-4 pb-6 border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                  <div className="flex justify-between text-sm">
                    <span className={isDark ? 'text-white/70' : 'text-gray-600'}>Articles ({cart.items_count || 0})</span>
                    <span className="font-medium">${cart.total?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className={isDark ? 'text-white/70' : 'text-gray-600'}>Livraison</span>
                    <span className="font-medium">Gratuite</span>
                  </div>
                </div>

                {/* Total */}
                <div className="flex justify-between text-lg font-bold my-6">
                  <span>Total</span>
                  <span className="text-rose-600">${cart.total?.toFixed(2) || '0.00'}</span>
                </div>

                {/* Boutons */}
                <div className="space-y-3">
                  <button
                    onClick={handleCheckout}
                    className="w-full px-4 py-3 bg-rose-600 text-white font-medium rounded-lg hover:bg-rose-700 transition-colors"
                  >
                    Procéder au paiement
                  </button>
                  <button
                    onClick={() => navigate('/shop')}
                    className={`w-full px-4 py-3 border font-medium rounded-lg transition-colors ${
                      isDark
                        ? 'border-white/20 text-white hover:bg-[#151516]'
                        : 'border-gray-300 text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    Continuer vos achats
                  </button>
                </div>

                {/* Sécurité */}
                <div className={`mt-6 pt-6 border-t text-xs text-center ${isDark ? 'border-white/10 text-white/60' : 'border-gray-100 text-gray-600'}`}>
                  <p>🔒 Paiement sécurisé par Stripe</p>
                </div>

              </div>
            </div>

          </div>
        )}

      </div>
    </div>
  );
};

export default Cart;
